import {
  fetchApp,
  fetchDiskAttachments,
  fetchService,
  selectActiveMountPointsByServiceId,
  selectEndpointsByServiceId,
  selectServiceById,
  updateServiceById,
} from "@app/deploy";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import { type SyntheticEvent, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  ButtonLinkDocs,
  ExternalLink,
  FormGroup,
  Input,
  Select,
} from "../shared";

const DEPLOYMENT_STRATEGY_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/apps/deploying-apps/releases/overview";
const HEALTHCHECK_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/health-checks";

const CONFIGURABLE_DEPLOYMENT_STRATEGY_OPTIONS = [
  { label: "Zero-Overlap", value: "disabled" },
  { label: "Zero-Downtime", value: "enabled" },
];
const LOAD_BALANCER_HEALTHCHECK_OPTIONS = [
  { label: "Load Balancer Healthcheck", value: "load-balancer" },
];

const endpointsAttachedBanner = (
  <Banner>
    Deployment strategy is enforced automatically, and healthcheck strategy is
    managed through this service's endpoint configuration, while this service
    has endpoints attached.
  </Banner>
);

const persistentDiskAttachedBanner = (
  <Banner>
    Deployment strategy is enforced automatically and cannot be modified while
    this service has a Persistent Disk attached.
  </Banner>
);

const deploymentStrategyDescription = (
  <>
    When an HTTP(S) endpoint is attached, Zero-Downtime deployments are
    enforced; TCP/TLS endpoints get Minimal-Downtime instead. Otherwise,
    configure Zero-Downtime (starts new containers before stopping old ones) or
    Zero-Overlap (stops old containers before starting new ones) —{" "}
    <ExternalLink href={DEPLOYMENT_STRATEGY_DOCS_URL}>see docs</ExternalLink>.
  </>
);

const healthcheckStrategyDescription = (
  <>
    When an endpoint is attached, the{" "}
    <ExternalLink href={HEALTHCHECK_DOCS_URL}>
      Load Balancer Healthcheck
    </ExternalLink>{" "}
    is used. Otherwise, configure Simple Healthcheck (works with either
    deployment strategy) or Docker Healthcheck (requires Zero-Downtime) —{" "}
    <ExternalLink href={DEPLOYMENT_STRATEGY_DOCS_URL}>see docs</ExternalLink>.
  </>
);

/**
 * The strategy the backend enforces when the service's configuration leaves no
 * choice. A Persistent Disk can only be mounted into one container at a time,
 * so disk-attached services always stop the old container before starting the
 * new one, regardless of endpoint type or the Zero-Downtime setting.
 */
const getEnforcedDeploymentStrategy = (
  hasActiveDisk: boolean,
  endpointEnforcesZeroDowntime: boolean,
) => {
  if (hasActiveDisk) {
    return "Zero-Overlap";
  }
  if (endpointEnforcesZeroDowntime) {
    return "Zero-Downtime";
  }
  return "Minimal-Downtime";
};

const getHealthcheckValue = (
  hasEndpoints: boolean,
  naiveHealthCheck: boolean,
  zeroDowntimeEnabled: boolean,
) => {
  if (hasEndpoints) {
    return "load-balancer";
  }
  if (naiveHealthCheck) {
    return "simple";
  }
  if (zeroDowntimeEnabled) {
    return "docker";
  }
  return "none";
};

export const AppDetailServiceSettingsPage = () => {
  const dispatch = useDispatch();
  const { id = "", serviceId = "" } = useParams();
  useQuery(fetchApp({ id }));
  useQuery(fetchService({ id: serviceId }));
  useQuery(fetchDiskAttachments());
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByServiceId(s, { serviceId: service.id }),
  );
  const activeMountPoints = useSelector((s) =>
    selectActiveMountPointsByServiceId(s, { serviceId: service.id }),
  );

  const [nextService, setNextService] = useState(service);
  useEffect(() => {
    setNextService(service);
  }, [service.id]);

  const hasEndpoints = endpoints.length > 0;
  const hasActiveDisk = activeMountPoints.length > 0;
  const endpointEnforcesZeroDowntime = endpoints.some((e) =>
    ["http", "http_proxy_protocol", "grpc"].includes(e.type),
  );
  const deploymentStrategyLocked = hasEndpoints || hasActiveDisk;
  const enforcedDeploymentStrategy = getEnforcedDeploymentStrategy(
    hasActiveDisk,
    endpointEnforcesZeroDowntime,
  );
  const zeroDowntimeAvailable = !hasActiveDisk;
  const zeroDowntimeEnabled =
    nextService.forceZeroDowntime && zeroDowntimeAvailable;

  const configuredDeploymentStrategy = nextService.forceZeroDowntime
    ? "enabled"
    : "disabled";
  const deploymentStrategyValue = deploymentStrategyLocked
    ? enforcedDeploymentStrategy
    : configuredDeploymentStrategy;
  const healthcheckValue = getHealthcheckValue(
    hasEndpoints,
    nextService.naiveHealthCheck,
    zeroDowntimeEnabled,
  );

  const persistentDiskBanner = hasActiveDisk
    ? persistentDiskAttachedBanner
    : null;
  const deploymentStrategyBanner = hasEndpoints
    ? endpointsAttachedBanner
    : persistentDiskBanner;

  const enforcedDeploymentStrategyOptions = [
    { label: enforcedDeploymentStrategy, value: enforcedDeploymentStrategy },
  ];
  const deploymentStrategyOptions = deploymentStrategyLocked
    ? enforcedDeploymentStrategyOptions
    : CONFIGURABLE_DEPLOYMENT_STRATEGY_OPTIONS;

  const configurableHealthcheckOptions = [
    {
      label: zeroDowntimeEnabled
        ? "No Healthcheck (requires Zero-Overlap deployments)"
        : "No Healthcheck",
      value: "none",
      disabled: zeroDowntimeEnabled,
    },
    {
      label: zeroDowntimeEnabled
        ? "Docker Healthcheck"
        : "Docker Healthcheck (requires Zero-Downtime deployments)",
      value: "docker",
      disabled: !zeroDowntimeEnabled,
    },
    { label: "Simple Healthcheck (30s)", value: "simple" },
  ];
  const healthcheckOptions = hasEndpoints
    ? LOAD_BALANCER_HEALTHCHECK_OPTIONS
    : configurableHealthcheckOptions;

  const action = updateServiceById({ ...nextService });
  const modifyLoader = useLoader(action);
  const cancelChanges = () => setNextService(service);
  const changesExist =
    service.forceZeroDowntime !== nextService.forceZeroDowntime ||
    service.naiveHealthCheck !== nextService.naiveHealthCheck ||
    service.restartFreeScaling !== nextService.restartFreeScaling ||
    service.stopTimeout !== nextService.stopTimeout;

  const onSubmitForm = (e: SyntheticEvent) => {
    e.preventDefault();
    dispatch(action);
  };

  return (
    <Box>
      <form onSubmit={onSubmitForm} className="flex flex-col gap-4">
        <BannerMessages {...modifyLoader} className="mb-4" />
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-start">
            <h1 className="text-lg text-gray-500 mb-4">
              Service Deployment Settings
            </h1>
            <ButtonLinkDocs href={DEPLOYMENT_STRATEGY_DOCS_URL} />
          </div>
          <div className="flex flex-col gap-2">
            {deploymentStrategyBanner}
            <FormGroup
              splitWidthInputs
              description={deploymentStrategyDescription}
              label="Deployment Strategy"
              htmlFor="deployment-strategy"
            >
              <Select
                id="deployment-strategy"
                disabled={deploymentStrategyLocked}
                value={deploymentStrategyValue}
                onSelect={(opt) =>
                  setNextService({
                    ...nextService,
                    forceZeroDowntime: opt.value === "enabled",
                  })
                }
                options={deploymentStrategyOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description={healthcheckStrategyDescription}
              label="Healthcheck Strategy"
              htmlFor="healthcheck-strategy"
            >
              <Select
                id="healthcheck-strategy"
                disabled={hasEndpoints}
                value={healthcheckValue}
                onSelect={(opt) =>
                  setNextService({
                    ...nextService,
                    naiveHealthCheck: opt.value === "simple",
                  })
                }
                options={healthcheckOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description="The time in seconds to wait for the old release's containers to stop before killing them. Must be between 1 and 900 seconds."
              label="Stop Timeout"
              htmlFor="stop-timeout"
              className="mt-4"
            >
              <Input
                id="stop-timeout"
                type="number"
                min={0}
                max={900}
                value={nextService.stopTimeout ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsedValue =
                    value === "" ? null : Number.parseInt(value);
                  setNextService({
                    ...nextService,
                    stopTimeout:
                      parsedValue === null
                        ? null
                        : Math.min(Math.max(0, parsedValue), 900),
                  });
                }}
              />
            </FormGroup>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-lg text-gray-500 mb-4">Autoscaling Settings</h1>
          <div className="flex flex-col gap-2">
            <FormGroup
              splitWidthInputs
              description="For autoscaling events only. Incremental Replacement only adds or removes the exact number of containers needed to reach your desired total. Full Replacement will always replace all existing containers."
              label="Container Replacement Strategy"
              htmlFor="restart-free-scaling"
            >
              <Select
                id="restart-free-scaling"
                value={nextService.restartFreeScaling ? "enabled" : "disabled"}
                onSelect={(opt) =>
                  setNextService({
                    ...nextService,
                    restartFreeScaling: opt.value === "enabled",
                  })
                }
                options={[
                  {
                    label: "Incremental Replacement (recommended)",
                    value: "enabled",
                  },
                  { label: "Full Replacement", value: "disabled" },
                ]}
              />
            </FormGroup>
          </div>
        </div>
        <div className="flex mt-4">
          <Button
            name="settings-save"
            className="w-40 flex font-semibold"
            type="submit"
            disabled={!changesExist}
            isLoading={modifyLoader.isLoading}
          >
            Save Changes
          </Button>
          {changesExist ? (
            <Button
              className="w-40 ml-2 flex font-semibold"
              onClick={cancelChanges}
              variant="white"
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </form>
    </Box>
  );
};
